import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Download, ArrowLeft, Send } from 'lucide-react';
import { RichTextEditor } from '../RichTextEditor';
import { EmailAttachmentGallery } from '../EmailAttachmentGallery';
import { getQuoteEmailPreview } from '../../services/api';

export function EmailModal({ isOpen, onClose, quote, onSend, loading }) {
  const [formData, setFormData] = useState({
    recipient1: '', recipient2: '', recipient3: '',
    subject: '', message: '', include_attachments: true
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState(new Set());

  // Build gallery sources: the quote itself (for newly-uploaded files) plus each description.
  const sources = useMemo(() => {
    if (!quote?.id) return [];
    const descs = quote.descriptions || [];
    return [
      { entity_type: 'quote', entity_id: quote.id },
      ...descs.map(d => ({ entity_type: 'quote_description', entity_id: d.id })),
    ];
  }, [quote?.id, quote?.descriptions]);

  const uploadTo = quote?.id ? { entity_type: 'quote', entity_id: quote.id } : null;

  useEffect(() => {
    if (quote && isOpen) {
      setLoadingPreview(true);
      getQuoteEmailPreview(quote.id).then(res => {
        setFormData(prev => ({ ...prev, ...res.data }));
      }).catch(() => {
        setFormData(prev => ({
          ...prev,
          subject: `Quote for ${quote.property_address || 'Project'}`,
          message: `<p>Dear ${quote.client_name || 'Customer'},</p><p>Please find attached our quotation for your review.</p>`
        }));
      }).finally(() => setLoadingPreview(false));
    }
  }, [quote, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.recipient1) { toast.error('Please enter at least one recipient'); return; }
    onSend({
      ...formData,
      included_photo_ids: Array.from(selectedAttachmentIds)
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <div className="text-xs text-slate-500">Quote &gt; {quote?.quote_number} &gt; Send Email</div>
            <h2 className="font-semibold">Email Quote</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {loadingPreview ? (
          <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-primary" /></div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1">Recipient 1 *</label>
              <input type="text" value={formData.recipient1} onChange={(e) => setFormData({...formData, recipient1: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" placeholder="email@example.com" data-testid="email-recipient1" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Recipient 2</label>
              <input type="text" value={formData.recipient2} onChange={(e) => setFormData({...formData, recipient2: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" placeholder="email@example.com" data-testid="email-recipient2" />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Recipient 3</label>
              <input type="text" value={formData.recipient3} onChange={(e) => setFormData({...formData, recipient3: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" placeholder="email@example.com" data-testid="email-recipient3" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Subject</label>
            <input type="text" value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" data-testid="email-subject" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Message</label>
            <RichTextEditor value={formData.message} onChange={(val) => setFormData({...formData, message: val})} />
          </div>

          {/* PDF Attachment toggle */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium">PDF:</label>
            <input type="checkbox" checked={formData.include_attachments} onChange={e => setFormData({...formData, include_attachments: e.target.checked})} className="w-4 h-4" data-testid="email-attachments" />
            {formData.include_attachments && (
              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1 rounded text-sm">
                <Download size={14} className="text-red-500" /> Quote_{quote?.quote_number}.pdf
              </div>
            )}
          </div>

          {/* Shared gallery: existing description attachments + ability to upload more (saved on the quote) */}
          {uploadTo && (
            <EmailAttachmentGallery
              sources={sources}
              uploadTo={uploadTo}
              selectedIds={selectedAttachmentIds}
              onSelectionChange={setSelectedAttachmentIds}
              title="Photos & Files"
              emptyHint='No attachments yet.'
            />
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded flex items-center gap-2">
              <ArrowLeft size={16} /> Cancel
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-primary text-white rounded flex items-center gap-2" data-testid="send-email-btn">
              {loading && <Loader2 size={16} className="animate-spin" />}
              <Send size={16} /> Send
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

// Create Deposit Invoice Modal
